const normalizeLookupKey = (value) =>
  String(value ?? '')
    .trim()
    .replace(/\./g, '')
    .replace(/[\s_-]+/g, '')
    .toLowerCase();

const ARABIC_TEXT_LATIN_DIGITS_LOCALE = 'ar-SY-u-nu-latn';
const LATIN_DIGITS_LOCALE = 'en-US';

const ORDER_STATUS_LABELS = {
  pending: 'معلق',
  accepted: 'مقبول',
  delivered: 'مسلّم',
  cancelled: 'ملغي',
  canceled: 'ملغي',
  rejected: 'مرفوض',
  preparing: 'قيد التحضير',
  processing: 'قيد المعالجة',
  shipped: 'تم الشحن',
  ontheway: 'قيد التوصيل',
  outfordelivery: 'قيد التوصيل',
  completed: 'مكتمل',
  assigned: 'مُسند',
  new: 'جديد',
};

const PAYMENT_METHOD_LABELS = {
  cash: 'نقداً',
  cashondelivery: 'الدفع عند الاستلام',
  cod: 'الدفع عند الاستلام',
  card: 'بطاقة',
  creditcard: 'بطاقة ائتمانية',
  debitcard: 'بطاقة مصرفية',
  wallet: 'محفظة إلكترونية',
  points: 'نقاط',
  online: 'دفع إلكتروني',
  applepay: 'آبل باي',
  googlepay: 'جوجل باي',
  visa: 'فيزا',
  mastercard: 'ماستركارد',
};

const COUPON_TYPE_LABELS = {
  percentage: 'نسبة مئوية',
  fixedamount: 'مبلغ ثابت',
  freedelivery: 'توصيل مجاني',
};

const COUPON_SCOPE_LABELS = {
  all: 'كل الطلبات',
  vendor: 'بائع محدد',
  product: 'منتج محدد',
  category: 'فئة محددة',
};

const BUNDLE_TYPE_LABELS = {
  buyxgety: 'اشترِ X واحصل على Y',
  buyxforprice: 'اشترِ X بسعر محدد',
};

const BUNDLE_SCOPE_LABELS = {
  product: 'منتج',
  vendor: 'بائع',
  category: 'فئة',
};

const API_MESSAGE_LABELS = {
  couponcodeisrequired: 'رمز الكوبون مطلوب.',
  invalidcouponcode: 'رمز الكوبون غير صالح.',
  couponhasexpiredorisnotactiveyet: 'انتهت صلاحية الكوبون أو أنه غير مفعّل بعد.',
  couponusagelimitreached: 'تم الوصول إلى الحد الأقصى لاستخدام الكوبون.',
  youhavereachedyourusagelimitforthiscoupon: 'لقد وصلت إلى الحد الأقصى لاستخدام هذا الكوبون.',
  couponisnotapplicabletocurrentcartitems: 'الكوبون غير قابل للتطبيق على عناصر السلة الحالية.',
  vendorcouponrequiresallitemstobefromthesamevendor: 'كوبون البائع يتطلب أن تكون جميع العناصر من البائع نفسه.',
  freedeliverycouponapplied: 'تم تطبيق كوبون التوصيل المجاني.',
  couponconfigurationisinvalid: 'إعدادات الكوبون غير صالحة.',
  unsupportedcoupontype: 'نوع الكوبون غير مدعوم.',
  coupondoesnotprovideadiscountforthiscart: 'لا يوفّر الكوبون خصمًا لهذه السلة.',
  couponappliedsuccessfully: 'تم تطبيق الكوبون بنجاح.',
};

const translateFromMap = (value, labels) => {
  if (value === undefined || value === null || value === '') return '—';

  const translated = labels[normalizeLookupKey(value)];
  return translated || value;
};

export const getOrderStatusLabel = (status) => translateFromMap(status, ORDER_STATUS_LABELS);

export const getPaymentMethodLabel = (paymentMethod) =>
  translateFromMap(paymentMethod, PAYMENT_METHOD_LABELS);

export const getCouponTypeLabel = (discountType) =>
  translateFromMap(discountType, COUPON_TYPE_LABELS);

export const getCouponScopeLabel = (scope) =>
  translateFromMap(scope, COUPON_SCOPE_LABELS);

export const getBundleTypeLabel = (bundleType) =>
  translateFromMap(bundleType, BUNDLE_TYPE_LABELS);

export const getBundleScopeLabel = (scope) =>
  translateFromMap(scope, BUNDLE_SCOPE_LABELS);

export const getActiveStateLabel = (isActive, activeLabel = 'فعّال', inactiveLabel = 'معطّل') =>
  isActive ? activeLabel : inactiveLabel;

export const formatBundleRule = (deal) => {
  if (!deal) return '—';

  if (deal.bundleType === 'BuyXGetY') {
    return `اشترِ ${deal.buyQuantity} واحصل على ${deal.getQuantity} (${deal.getDiscountPercentage ?? 100}% خصم)`;
  }

  return `اشترِ ${deal.buyQuantity} بسعر ${deal.bundlePrice}`;
};

export const translateApiMessage = (message) => {
  if (!message || typeof message !== 'string') {
    return message;
  }

  const directTranslation = API_MESSAGE_LABELS[normalizeLookupKey(message)];
  if (directTranslation) {
    return directTranslation;
  }

  const minimumOrderMatch = message.match(/^Minimum order amount is\s+(.+?)\.?$/i);
  if (minimumOrderMatch) {
    return `الحد الأدنى لقيمة الطلب هو ${minimumOrderMatch[1]}.`;
  }

  return message;
};

export const toLatinDigits = (value) =>
  String(value ?? '')
    .replace(/[٠-٩]/g, (digit) => String(digit.charCodeAt(0) - 1632))
    .replace(/[۰-۹]/g, (digit) => String(digit.charCodeAt(0) - 1776));

export const formatNumber = (value, options = {}) => {
  const numericValue = Number(value ?? 0);

  if (!Number.isFinite(numericValue)) {
    return toLatinDigits(value);
  }

  return new Intl.NumberFormat(LATIN_DIGITS_LOCALE, options).format(numericValue);
};

export const formatMoney = (value, options = {}) =>
  `${formatNumber(value, { maximumFractionDigits: 0, ...options })} ل.س`;

export const formatDate = (value, options = {}) => {
  if (!value) return '—';

  return new Intl.DateTimeFormat(ARABIC_TEXT_LATIN_DIGITS_LOCALE, {
    dateStyle: 'short',
    ...options,
  }).format(new Date(value));
};

export const formatDateTime = (value, options = {}) => {
  if (!value) return '—';

  return new Intl.DateTimeFormat(ARABIC_TEXT_LATIN_DIGITS_LOCALE, {
    dateStyle: 'short',
    timeStyle: 'short',
    ...options,
  }).format(new Date(value));
};

export const formatTime = (value, options = {}) => {
  if (!value) return '—';

  return new Intl.DateTimeFormat(ARABIC_TEXT_LATIN_DIGITS_LOCALE, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    ...options,
  }).format(new Date(value));
};
